import { useLocation, useNavigate } from "react-router-dom";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useCallback } from "react";
import {
	buildSDPSearchPayload,
	getLastSearchData,
	normalizeDate,
	pickInitialValue,
	hasCustomBookingOccupancies,
} from "app/utils/smartDPUtils";
import {
	HOTEL_ONLY_PRODUCTS_FILTERS_KEY,
	QUOTATION_CODE_STATUS,
	RESOLUTION,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import PropTypes from "prop-types";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { unsetSDPProductsFilter as unsetSDPProductsFilterAction } from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterActionCreators";
import { getTopDestinations } from "app/reducers/shopSelector";
import HotelOnlySearchForm from "app/pages/HotelOnly/HotelOnlySearchForm/HotelOnlySearchForm";
import { resetHotelOnlyBookingFlowId as resetHotelOnlyBookingFlowIdAction } from "app/pages/HotelOnly/hotelOnlyActionCreator";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import withRouter from "app/utils/hocs/withRouter";
import { parse, stringify } from "qs";
import { useLoadingBar } from "app/features/loadingBar";

const HotelOnlySearchFormContainer = props => {
	const { track } = useAnalytics();

	const { onSubmit, skipListingRedirect } = props;

	const stateProps = useSelector(state => mapStateToProps(state, props), shallowEqual);
	const dispatch = useDispatch();
	const unsetSDPProductsFilter = useCallback(
		(...args) => dispatch(unsetSDPProductsFilterAction(...args)),
		[dispatch]
	);
	const resetHotelOnlyBookingFlowId = useCallback(
		(...args) => dispatch(resetHotelOnlyBookingFlowIdAction(...args)),
		[dispatch]
	);

	const { search } = useLocation();

	const navigate = useNavigate();
	const { pulse } = useLoadingBar();

	const handleFormSubmission = useCallback(
		({ values, pathname = "" }) => {
			let newQueryParams = buildSDPSearchPayload(values, track);
			pulse();
			unsetSDPProductsFilter(HOTEL_ONLY_PRODUCTS_FILTERS_KEY.BUDGET);

			// Réinitialiser le bookingFlowId à chaque nouvelle recherche HotelOnly.
			resetHotelOnlyBookingFlowId();

			if (onSubmit) {
				onSubmit(values);
			}

			if (skipListingRedirect) {
				return;
			}

			if (pathname.includes("/hotelonly/listing")) {
				const currentParams = parse(search, { ignoreQueryPrefix: true });
				const newParams = parse(newQueryParams, { ignoreQueryPrefix: true });

				// After buildSDPSearchPayload produces the new query string, compare the new destination
				// with the current URL:
				// - If the current URL has accommodationId, the new query does NOT already have one, and destinationResortCode
				// is unchanged → preserve the existing accommodationId.
				// - Otherwise (resort changed, or new hotel selected) → let the new query string through as-is.
				if (
					currentParams.accommodationId &&
					!newParams.accommodationId &&
					newParams.destinationResortCode === currentParams.destinationResortCode
				) {
					newQueryParams = stringify(
						{ ...newParams, accommodationId: currentParams.accommodationId },
						{ encodeValuesOnly: true, arrayFormat: "indices" }
					);
				}

				navigate({ pathname, search: newQueryParams }, { replace: true });
			} else {
				navigate({ pathname: "/hotelonly/listing", search: newQueryParams });
			}
		},
		[search, skipListingRedirect, onSubmit]
	);

	return (
		<div className="search-form__container">
			<HotelOnlySearchForm {...props} {...stateProps} onSuccess={handleFormSubmission} />
		</div>
	);
};

HotelOnlySearchFormContainer.propTypes = {
	fetchHotelOnlySearchFormAvailability: PropTypes.func,
	resetHotelOnlyBookingFlowId: PropTypes.func,
	unsetSDPProductsFilter: PropTypes.func,
	destinations: PropTypes.array,
	isMobile: PropTypes.bool,
	brand: PropTypes.string,
	disableFormPersistance: PropTypes.bool, // utilisé pour éviter le flicker des champs entre la page merch et la home SDP
	onSubmit: PropTypes.func,
	skipListingRedirect: PropTypes.bool,
	skipLastSearchFallback: PropTypes.bool,
	isCompact: PropTypes.bool,
	isLoading: PropTypes.bool,
	submitButtonLabel: PropTypes.node,
};

const mapStateToProps = (state, props) => {
	const { booking } = state;
	const { disableFormPersistance, skipLastSearchFallback } = props;
	const isMerchPage = props?.location?.pathname?.includes("/merch");
	const lastSearch = getLastSearchData(state, WEBSITE_TUNNEL_TYPES.HOTEL_ONLY);

	const initialValues = {
		travelDates: {
			departureDate: "",
			endDate: "",
		},
	};

	const lastDestination = lastSearch?.searchedAccommodation
		? {
				code: lastSearch.searchedAccommodation.id,
				label: lastSearch.searchedAccommodation.name,
				isHotel: true,
				places: lastSearch.searchedAccommodation.places,
			}
		: lastSearch?.destinationResort;

	// En merch, la page fixe destination/dates mais on garde l'historique pour le reste.
	const canUseLastSearchForMainFields = !disableFormPersistance && !isMerchPage;
	const canUseLastSearchForOtherFields = !disableFormPersistance;

	// Petit helper pour garder la priorité booking -> requête -> dernière recherche sans répéter la config.
	const pickValue = params =>
		pickInitialValue({
			disableFormPersistance,
			skipLastSearchFallback,
			...params,
		});

	const normalizeValue = date => (date ? normalizeDate(date) : undefined);

	// 1. On privilégie la destination persistée, sinon on retombe sur la dernière recherche.
	const destinationResort = pickValue({
		bookingValue: booking?.destinationResort,
		lastSearchValue: lastDestination,
		allowLastSearch: canUseLastSearchForMainFields,
		isValueValid: value => !isEmpty(value),
	});

	const hasDestination = Boolean(destinationResort);

	if (hasDestination) {
		initialValues.destinationResort = destinationResort;
	}

	// 2. Dates et durée ne sont reprises que lorsque la destination est définie et la persistance active.
	if (hasDestination && !disableFormPersistance) {
		const departureDate = pickValue({
			bookingValue: normalizeValue(booking?.departureDate),
			lastSearchValue: normalizeValue(lastSearch?.departureDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (departureDate) {
			initialValues.travelDates.departureDate = departureDate;
		}

		const endDate = pickValue({
			bookingValue: normalizeValue(booking?.endDate),
			lastSearchValue: normalizeValue(lastSearch?.endDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (endDate) {
			initialValues.travelDates.endDate = endDate;
		}

		const bookingDuration = booking?.duration?.value;
		const lastDuration = lastSearch?.duration?.value;

		const durationCode = pickValue({
			bookingValue: bookingDuration,
			lastSearchValue: lastDuration,
			allowLastSearch: canUseLastSearchForMainFields,
			isValueValid: value => !isNil(value),
		});

		if (!isNil(durationCode)) {
			initialValues.duration = { code: durationCode };
		}
	}

	// 3. On persiste les occupancies uniquement si l'utilisateur est sorti de la config par défaut.
	if (!disableFormPersistance) {
		const bookingOccupancies = hasCustomBookingOccupancies(booking)
			? booking?.occupancies
			: undefined;
		const lastSearchOccupancies =
			lastSearch?.occupancies?.length > 0 ? lastSearch?.occupancies : undefined;

		const occupancies = pickValue({
			bookingValue: bookingOccupancies,
			lastSearchValue: lastSearchOccupancies,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => Array.isArray(value) && value.length > 0,
		});

		if (occupancies) {
			initialValues.occupancies = occupancies;
		}
	}

	return {
		initialValues,
		destinations: state.smartDP.destinations,
		topDestinationsList: getTopDestinations(state) || [],
		validateOnMount: state.smartDP.status === QUOTATION_CODE_STATUS.BAD_REQUEST,
		isMobile: [RESOLUTION.SMALL, RESOLUTION.MEDIUM].includes(state?.resolution),
		brand: state.brand.code,
	};
};

export default withRouter(HotelOnlySearchFormContainer);
